import { useCallback, useEffect, useRef } from "react";
import { VIN_PATTERN } from "@lending/hooks/queries/AutoLoanApplication/types";
import { useDecodeVinQuery } from "@lending/hooks/queries/useDecodeVinQuery";

/** Vehicle details decoded from a VIN; fields are null when NHTSA has no data. */
interface DecodedVehicleDetails {
  year: string | null;
  make: string | null;
  model: string | null;
  trim: string | null;
}

type VinLookupStatus =
  /** No complete VIN to look up (or the lookup already applied). */
  | "idle"
  /** A decode request is in flight. */
  | "decoding"
  /** The current VIN decoded to usable details. */
  | "matched"
  /** The current VIN had no match, or the decode provider failed. */
  | "noMatch";

interface UseVinLookupOptions {
  /** The VIN input's current value. */
  vin: string;
  /** Whether VIN collection is enabled for this institution. */
  enabled: boolean;
  /**
   * Called exactly once per VIN whose decode returned identifying details
   * (year, make, or model). Not called again for a VIN it already applied,
   * so a decode never overwrites the applicant's later edits.
   */
  onDecoded: (decoded: DecodedVehicleDetails) => void;
}

/**
 * Auto-decodes a complete 17-character VIN and reports the lookup's status.
 *
 * Wraps `useDecodeVinQuery` with the AutoDetails-specific orchestration:
 * fire only on complete VINs, apply each decode result once via `onDecoded`,
 * and skip VINs marked as already applied (e.g. one restored from the saved
 * application via `markApplied`).
 */
function useVinLookup({ vin, enabled, onDecoded }: UseVinLookupOptions) {
  const isVinComplete = VIN_PATTERN.test(vin);

  // The VIN whose decode result (or saved value) is already reflected in the
  // form; lookups for it are skipped.
  const appliedVinRef = useRef<string | null>(null);

  const markApplied = useCallback((appliedVin: string | null) => {
    appliedVinRef.current = appliedVin;
  }, []);

  const { decodedVehicle, isDecoding, isDecodeError } = useDecodeVinQuery(vin, {
    enabled: enabled && isVinComplete && appliedVinRef.current !== vin,
  });

  const decodeHadNoMatch =
    decodedVehicle != null &&
    decodedVehicle.year == null &&
    decodedVehicle.make == null &&
    decodedVehicle.model == null &&
    decodedVehicle.trim == null;

  useEffect(() => {
    if (!enabled || !isVinComplete || !decodedVehicle) return;
    if (appliedVinRef.current === vin) return;
    const hasIdentifyingDetails =
      decodedVehicle.year != null || decodedVehicle.make != null || decodedVehicle.model != null;
    if (!hasIdentifyingDetails) return;
    appliedVinRef.current = vin;
    onDecoded(decodedVehicle);
  }, [enabled, isVinComplete, decodedVehicle, vin, onDecoded]);

  let status: VinLookupStatus = "idle";
  if (enabled && isVinComplete) {
    if (isDecoding) {
      status = "decoding";
    } else if (isDecodeError || decodeHadNoMatch) {
      status = "noMatch";
    } else if (decodedVehicle != null) {
      status = "matched";
    }
  }

  return { status, markApplied };
}

export { useVinLookup };
export type { DecodedVehicleDetails, VinLookupStatus };
